import React, { useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { fetchUser } from '@/src/actions/admin/v2/adminUsersActions'
import { UserWithAdminInfo } from '@/src/reducers/admin/v2/allUsersReducerV2'
import { getPlaidOnlyFinancialAccountsByUserId } from '@/src/selectors/financeSelectors'
import { useReselector } from '@/src/utils/sharedHooks'
import { FinancialAccountWithAdminInfo } from '@/src/reducers/admin/financialAccountsReducer'
import { BookkeepingReport } from '@/src/reducers/finances/bookkeepingReportsReducer'
import { DATE_FORMATS_LUXON } from '@/src/utils/dateHelpers'

export type TransactionRowModel = {
  id: number
  credit: string | null
  debit: string | null
  description: string | null
  manualUpload: boolean
  matched: boolean
  reconciliationId: number
  date: string
}

export type ReactClickSignature = (
  _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null
) => void

export interface TransactionCollection {
  [key: string]: TransactionRowModel
}

export const useAutoReconCUBK = ({
  userId,
  bookkeepingReport,
}: {
  userId: number
  account?: FinancialAccountWithAdminInfo
  bookkeepingReport?: BookkeepingReport
}) => {
  const [user, setUser] = useState<UserWithAdminInfo>()
  const [loading, setLoading] = useState(true)

  const plaidAccounts = useReselector(
    getPlaidOnlyFinancialAccountsByUserId,
    userId
  )

  useEffect(() => {
    const fetchAdminUser = async () => {
      const result = await fetchUser({
        id: userId,
        select: ['catchUpBookkeepingStatus'],
      })
      setUser(result)
      setLoading(false)
    }
    fetchAdminUser()
  }, [userId])

  const isEligible = useMemo(() => {
    if (!user) return false
    const { catchUpBookkeepingStatus } = user
    if (!catchUpBookkeepingStatus) return false
    if (plaidAccounts.length === 0) return false

    return catchUpBookkeepingStatus.status === 'incomplete'
  }, [user, plaidAccounts])

  const autoReconPeriod = useMemo(() => {
    if (!bookkeepingReport || !isEligible) return null
    const { date } = bookkeepingReport
    const startDate = DateTime.fromFormat(date, DATE_FORMATS_LUXON.YEAR_MONTH)
      .plus({ month: 1 })
      .startOf('month')
    const previousMonthDate = DateTime.now()
      .minus({ month: 1 })
      .startOf('month')
    if (startDate > previousMonthDate) return null
    return [startDate, previousMonthDate]
  }, [bookkeepingReport, isEligible])

  const autoReconPeriodFormattedShort = useMemo(() => {
    if (!autoReconPeriod) return null
    const [startDate, previousMonthDate] = autoReconPeriod
    if (startDate.equals(previousMonthDate)) return startDate.monthShort
    return `${startDate.monthShort} - ${previousMonthDate.monthShort}`
  }, [autoReconPeriod])

  const autoReconPeriodFormattedLong = useMemo(() => {
    if (!autoReconPeriod) return null
    const [startDate, previousMonthDate] = autoReconPeriod
    if (startDate.equals(previousMonthDate))
      return `${startDate.monthLong} ${startDate.year}`
    return `${startDate.monthLong} - ${previousMonthDate.monthLong} ${previousMonthDate.year}`
  }, [autoReconPeriod])

  return {
    isEligible,
    autoReconPeriod,
    autoReconPeriodFormattedShort,
    autoReconPeriodFormattedLong,
    loading,
  }
}

export const isAfterAutoReconPeriod = (isoString: string) => {
  const previousMonth = DateTime.utc().minus({ month: 1 }).endOf('month')
  const reportDate = DateTime.fromISO(isoString, { zone: 'utc' })
  return reportDate > previousMonth
}
